import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { PipelineStage, Setting } from '@/lib/types';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Modal from '@/components/ui/Modal';
import { Plus, Trash2, Download, Loader2 } from 'lucide-react';
import { AjusteKit, AjusteMinimos, AjusteModelos } from '@/components/dispositivos/AjustesDispositivos';
import { olvidarCatalogosDispositivos } from '@/components/dispositivos/catalogos';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<'stages' | 'sources' | 'zones' | 'types' | 'puestos' | 'dispositivos'>('stages');

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold mb-6">Configuración</h1>

      <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
        {[
          { key: 'stages', label: 'Fases del pipeline' },
          { key: 'sources', label: 'Fuentes de captación' },
          { key: 'zones', label: 'Zonas' },
          { key: 'types', label: 'Tipos de propiedad' },
          { key: 'puestos', label: 'Puestos de trabajo' },
          { key: 'dispositivos', label: 'Dispositivos' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key ? 'bg-sereno-dark text-white' : 'bg-white text-sereno-dark/60 hover:bg-sereno-blue/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'stages' && <PipelineStagesSettings />}
      {activeTab === 'sources' && <ListSettings settingId="sources" title="Fuentes de captación" />}
      {activeTab === 'zones' && <ListSettings settingId="zones" title="Zonas" />}
      {activeTab === 'types' && <ListSettings settingId="property_types" title="Tipos de propiedad" />}
      {/* Los puestos que se pueden marcar en la ficha de un candidato, y el
          plazo que se conserva su currículum antes de que caduque. */}
      {activeTab === 'puestos' && (
        <div className="space-y-6">
          <ListSettings settingId="puestos" title="Puestos de trabajo" />
          <ConservacionCandidatos />
        </div>
      )}
      {/* Los cuatro ajustes del inventario de hardware. Los tipos de incidencia
          son id + nombre y usan el editor genérico; el resto llevan más campos. */}
      {activeTab === 'dispositivos' && (
        <div className="space-y-6">
          <AjusteModelos />
          <ListSettings settingId="dispositivos_incidencia_tipos" title="Tipos de incidencia" onCambio={olvidarCatalogosDispositivos} />
          <AjusteMinimos />
          <AjusteKit />
        </div>
      )}

      <div className="mt-8 card">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">Exportar datos</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href="/api/export/owners" className="btn-secondary flex items-center gap-2 text-sm">
            <Download size={16} /> Exportar propietarios (Excel)
          </a>
          <a href="/api/export/properties" className="btn-secondary flex items-center gap-2 text-sm">
            <Download size={16} /> Exportar propiedades (Excel)
          </a>
        </div>
      </div>
    </div>
  );
}

function PipelineStagesSettings() {
  const { data: stages, loading, refresh } = useApi<PipelineStage[]>(() => api.getStages(), []);
  const [showForm, setShowForm] = useState(false);
  const [editStage, setEditStage] = useState<PipelineStage | null>(null);

  const handleCreate = async (data: any) => {
    await api.createStage(data);
    setShowForm(false);
    refresh();
  };

  const handleUpdate = async (data: any) => {
    if (editStage) {
      await api.updateStage(editStage.id, data);
      setEditStage(null);
      refresh();
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('¿Eliminar esta fase?')) {
      await api.deleteStage(id);
      refresh();
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Fases del pipeline</h3>
        <button onClick={() => setShowForm(true)} className="btn-secondary text-sm flex items-center gap-1">
          <Plus size={14} /> Añadir fase
        </button>
      </div>

      <div className="space-y-2">
        {stages?.map(stage => (
          <div key={stage.id} className="flex flex-wrap items-center gap-2 sm:gap-3 p-3 rounded-lg border border-gray-100 hover:bg-sereno-blue/10">
            <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
            <span className="flex-1 text-sm font-medium min-w-0">{stage.name}</span>
            <span className="text-xs text-sereno-dark/40">Orden: {stage.order}</span>
            {stage.isDefault && <span className="badge bg-sereno-blue text-sereno-dark text-xs">Por defecto</span>}
            {stage.isClosed && <span className="badge bg-green-100 text-green-700 text-xs">Cerrado</span>}
            {stage.isLost && <span className="badge bg-red-100 text-red-700 text-xs">Perdido</span>}
            <button onClick={() => setEditStage(stage)} className="text-sereno-dark/30 hover:text-sereno-dark text-xs">Editar</button>
            <button onClick={() => handleDelete(stage.id)} className="text-red-300 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <Modal isOpen={showForm || !!editStage} onClose={() => { setShowForm(false); setEditStage(null); }} title={editStage ? 'Editar fase' : 'Nueva fase'}>
        <StageForm stage={editStage || undefined} onSubmit={editStage ? handleUpdate : handleCreate} onCancel={() => { setShowForm(false); setEditStage(null); }} />
      </Modal>
    </div>
  );
}

function StageForm({ stage, onSubmit, onCancel }: { stage?: PipelineStage; onSubmit: (d: any) => Promise<void>; onCancel: () => void }) {
  const [form, setForm] = useState({
    name: stage?.name || '',
    order: stage?.order || 1,
    color: stage?.color || '#6B7280',
    isDefault: stage?.isDefault || false,
    isClosed: stage?.isClosed || false,
    isLost: stage?.isLost || false,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div><label className="label">Nombre *</label><input className="input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><label className="label">Orden</label><input type="number" className="input" value={form.order} onChange={e => setForm(f => ({ ...f, order: Number(e.target.value) }))} /></div>
        <div><label className="label">Color</label><input type="color" className="input h-10" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} /></div>
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} /><span className="text-sm">Por defecto</span></label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={form.isClosed} onChange={e => setForm(f => ({ ...f, isClosed: e.target.checked }))} /><span className="text-sm">Cerrado (ganado)</span></label>
        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="w-4 h-4" checked={form.isLost} onChange={e => setForm(f => ({ ...f, isLost: e.target.checked }))} /><span className="text-sm">Perdido</span></label>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Guardando...' : 'Guardar'}</button>
      </div>
    </form>
  );
}

/**
 * Cuántos meses se guarda un currículum antes de que el barrido lo marque.
 *
 * Vive en el ajuste `candidatos`, en una clave suelta, y no en `ListSettings`
 * como los demás: aquello es una lista de pares id/etiqueta, y aquí lo que hay
 * es un número. Enseñarlo como una lista de un elemento donde la «etiqueta» es
 * «12» invita a romperlo.
 */
function ConservacionCandidatos() {
  const { data: setting, loading, refresh } = useApi<Setting>(
    () => api.getSetting('candidatos'),
    [],
  );
  const [meses, setMeses] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const actual = setting?.values?.find(v => v.id === 'retencion_meses')?.label || '12';
  const valor = meses || actual;
  const numero = Number(valor);
  const valido = Number.isInteger(numero) && numero >= 1 && numero <= 120;

  if (loading) return <LoadingSpinner />;

  const guardar = async () => {
    if (!valido) return;
    setGuardando(true);
    setGuardado(false);
    try {
      await api.updateSetting('candidatos', {
        values: [{ id: 'retencion_meses', label: String(numero) }],
      });
      setGuardado(true);
      refresh();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-2">
        Conservación de currículums
      </h3>
      <p className="text-sm text-sereno-dark/60 mb-4">
        Cuánto tiempo se guarda el CV de un candidato desde que llega. Al cumplirse, aparece en la
        pestaña «Caducados» de Candidatos; no se borra nada solo. Los contratados no caducan.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label" htmlFor="retencion-meses">Meses</label>
          <input
            id="retencion-meses"
            className="input h-11 w-28"
            type="number"
            min={1}
            max={120}
            value={valor}
            onChange={e => { setMeses(e.target.value); setGuardado(false); }}
          />
        </div>
        <button
          onClick={guardar}
          disabled={guardando || !valido || String(numero) === actual}
          className="btn-primary h-11 text-sm flex items-center gap-2"
        >
          {guardando && <Loader2 size={16} className="animate-spin" />}
          Guardar
        </button>
        {guardado && <span className="text-sm text-green-700 pb-3">Guardado.</span>}
        {!valido && <span className="text-sm text-red-600 pb-3">Entre 1 y 120 meses.</span>}
      </div>
    </div>
  );
}

function ListSettings({ settingId, title, onCambio }: { settingId: string; title: string; onCambio?: () => void }) {
  const { data: setting, loading, refresh } = useApi<Setting>(() => api.getSetting(settingId), [settingId]);
  const [newItem, setNewItem] = useState({ id: '', label: '' });
  const [saving, setSaving] = useState(false);

  if (loading || !setting) return <LoadingSpinner />;

  const handleAdd = async () => {
    if (!newItem.id || !newItem.label) return;
    setSaving(true);
    try {
      const updatedValues = [...setting.values, { id: newItem.id, label: newItem.label }];
      await api.updateSetting(settingId, { values: updatedValues });
      setNewItem({ id: '', label: '' });
      onCambio?.();
      refresh();
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (itemId: string) => {
    const updatedValues = setting.values.filter(v => v.id !== itemId);
    await api.updateSetting(settingId, { values: updatedValues });
    onCambio?.();
    refresh();
  };

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-4">{title}</h3>
      <div className="space-y-2 mb-4">
        {setting.values.map(item => (
          <div key={item.id} className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg border border-gray-100">
            <span className="text-xs text-sereno-dark/40 font-mono w-16 sm:w-24 truncate flex-shrink-0">{item.id}</span>
            <span className="flex-1 text-sm min-w-0 truncate">{item.label}</span>
            <button onClick={() => handleRemove(item.id)} className="text-red-300 hover:text-red-500"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input sm:flex-1" placeholder="ID (clave)" value={newItem.id} onChange={e => setNewItem(n => ({ ...n, id: e.target.value }))} />
        <input className="input sm:flex-1" placeholder="Nombre visible" value={newItem.label} onChange={e => setNewItem(n => ({ ...n, label: e.target.value }))} />
        <button onClick={handleAdd} disabled={saving} className="btn-primary">{saving ? '...' : 'Añadir'}</button>
      </div>
    </div>
  );
}
